/**
 * Filter View
 * @author tbroussard
 *
 * This view handles behaviors for setting filter search parameters and triggers
 * a global event to notify listeners when it's time to execute a search.
 */
import $ from 'jquery';
import Backbone from 'backbone';
import BaseView from 'BaseView';
import dateFormatWidget from 'widgets/dateFormat';
import SessionTimer from 'widgets/sessionTimer';
import createDateRangePicker from 'view/activity/dateRangePicker/desktopView';

import AdvancedSearchView from 'view/activity/filters/advancedSearchView';
import FreeTextSearchView from 'view/activity/filters/freeTextSearchView';
import FilterTagsView from 'view/activity/filters/filterTagsView';

import 'bootstrap-dropdown';
import 'bootstrap-tooltip';
import 'bootstrap-popover';

export default BaseView.extend({

	el: '#js_filterMessageView',

	events: {
		'click .js_filters-searchBtn': 'onFilterView', // Handles View action for filter search
		'click .js_dateLabel': 'onCustomDateLabelClick',
		'click .js_filters-overpanelBtn': 'onClickFunnelIcon', // desktop/tablet behavior defined here,
		'click .js_new-filters-presetDate, .js_new-customDate-saveBtn': 'onFilterView',

		'submit #js_filters-form': (event) => event.preventDefault() // Ensures form will not submit
	},

	/**
	 * Initialize Filter View
	 *
	 * @param options - parameters passed to view when it's initialized
	 */
	initialize (options) {
		// Ensure timeFrameText is set in the model
		this.model.set('timeFrameText', this.$('.js_dateDropdown-label').html(), { silent: true });

		// Ensure that isDesktopView set in model
		this.model.set('isDesktopView', true);

		// Parse and set filter model attributes from data captured from the DOM
		this.serverRenderedUIData = this.getServerRenderedUIData();
		this.model.parseFormData(this.serverRenderedUIData);

		/* Keep UI form elements in sync with model changes by watch for
		 * changes in the model and update DOM, instead of re-rendering entire
		 * view */
		this.listenTo(this.model, 'change:startDate', this.updateFormValues);
		this.listenTo(this.model, 'change:endDate', this.updateFormValues);
		this.listenTo(this.model, 'change:transactionType', this.updateFormValues);
		this.listenTo(this.model, 'change:freeTextSearch', this.updateFormValues);
		this.listenTo(this.model, 'change:transactionFilters', this.updateFormValues);
		this.listenTo(this.model, 'change:selectedCurrency', this.filterSelectedCurrency);

		// Listen to "load more" event trigger from Activity page-level view
		this.listenTo(Backbone, 'activityCollection:loadMoreTransactions', this.triggerSearch);

		// js_dateFormat value is set by L10N as "mmddyy", etc.
		this.dateFormatString = $.trim(this.$('#js_dateFormat').val());
		this.monthHeaderFormatString = (
			$.trim(this.$('#js_monthHeaderFormat').val()) || 'MMMM YYYY'
		);

		// using single M/D so that we can enter digits w/o leading zero; 1/1/14 & 01/01/14 will both work
		// when strict parsing is enabled in moment
		this.dateInputDateFormatString = $.trim(this.$('#js_datePickerInputDateFormat').val()) || 'M/D/YY';

		this.model.setDateFormatter(dateFormatWidget(this.dateFormatString));

		this.setupDateRangePickerComponent();

		// initialize different filters
		this.freeTextSearchView = new FreeTextSearchView({parent: this, model: this.model});
		this.advancedSearchView = new AdvancedSearchView({parent: this, model: this.model});
		this.filterTagsView = new FilterTagsView({parent: this, model: this.model});

		// set the ValidateForm instance
		this.formView = options.filterValidator.createFilterValidator(this);

		// set the session timer instance (unfortunately is not a singleton)
		this.sessionTimer = new SessionTimer({});
	},

	createDateRangePicker,

	getDatePickerElement () {
		return this.$('.js_datePicker-body');
	},

	setupDateRangePickerComponent () {
		// setup custom date picker view
		let $datePickerParent = this.$('#js_datePicker');
		let $datePickerElement = this.getDatePickerElement();

		if ($datePickerElement.length > 0) {
			let labels = this.serverRenderedUIData.datePickerLocalization;
			labels.startDate = $datePickerParent.data('startLabel');
			labels.endDate = $datePickerParent.data('endLabel');
			this.datePicker = this.createDateRangePicker({
				el: $datePickerElement[0],
				labels,
				formats: {
					date: this.dateFormatString,
					monthHeader: this.monthHeaderFormatString,
					dateInput: this.dateInputDateFormatString
				},
				rtl: $('html').attr('dir') === 'rtl',
				startOfWeek: labels.firstDayOfTheWeek,
				minDate: this.model.get('startDateLimitMoment').utc().toDate(),
				maxDate: new Date(),
				customShortcuts: this.$el.find('.js_customDates-shortcut'),
				onDateChange: (event, { startDate, endDate }) => {
					this.model.setDateFromObject('startDate', startDate);
					this.model.setDateFromObject('endDate', endDate);

					if (this.model.has('isDesktopView') && this.model.get('isDesktopView')) {
						// Since date search, we need to backup the context
						if (this.model.has('freeTextSearch')) {
							this.model.set('_freeTextDateSearch', this.model.get('freeTextSearch'));
						}

						if (this.model.has('transactionFilter')) {
							this.model.set('_transactionFilterDateSearch', this.model.get('transactionFilter'));
						}
					}

					this.setupDataForDateSearch();
					this.onFilterView(event, true);
				}
			});
		}
	},

	/**
	 * Gets data from UI controls to set up the model after initial page load.
	 * Should only be called from initialize, because values that are grabbed
	 * from are not kept up-to-date.
	 *
	 * Note: this seems to have a slightly similar purpose with
	 * initializeRetainCustomSearch. The 2 functions could probably be merged.
	 *
	 * @returns object.transactionType
	 * @returns object.timeFrame
	 */
	getServerRenderedUIData () {
		let datePickerData = this.$('#js_datePicker').data(),
			datePickerLocalizationData;

		if (datePickerData) {
			datePickerLocalizationData = datePickerData.localizedData;
		}

		let currencyFilterData = $('.js_currencyFilter').data(),
			currencyCodes;

		if (currencyFilterData) {
			currencyCodes = currencyFilterData.currencyCodes;
		}

		return {
			transactionType: this.$('.js_filters-control-transactionType').val(),
			timeFrame: this.$('.js_filters-control-timeFrame').val(),
			freeTextSearch: this.$('.js_filters-control-freeTextSearch').val(),
			datePickerLocalization: datePickerLocalizationData,
			currencyCodes: currencyCodes
		};
	},

	/**
	 * Funnel icon in the page header clicked
	 * Show the desktop filters for skinny-sized browsers
	 */
	onClickFunnelIcon (event) {
		event.preventDefault();
		this.$('.js_filters-section').toggleClass('hidden-phone');
	},

	/**
	 * Handle "Save Dates" button getting clicked
	 */
	processDateInputs (event) {
		// Prevent filter form submission
		event.preventDefault();

		// if dates are not empty
		if (this.model.hasStartDate() && this.model.hasEndDate()) {
			if (!this.formView.validateBeforeSubmit(event)) {
				return false;
			}

			// dates are valid format, check if they meet biz rules
			const bizRulesValidation = this.model.checkDateRangeLimits();

			if (bizRulesValidation.bothDatesTooEarly) {
				// allow dropdown to close but don't let search continue
				return bizRulesValidation;
			}
		}

		// scroll to top - helpful for short screens
		$(window).scrollTop(0);

		return true;
	},

	/**
	 * Clear the free text search, filter search, and currency selector model properties when date search is initiated.
	 */
	unsetSearchFilters () {
		let isDesktopView = this.model.get('isDesktopView');

		if (this.model.has('freeTextSearch')) {
			this.model.unset('freeTextSearch');
		}

		if (this.model.has('transactionFilter')) {
			this.model.unset('transactionFilter');
		}

		this.model.set('isClearFreeTextSearch', false);
		this.model.set('isClearFilterSelection', false);
		this.model.set('selectedCurrency', 'ALL', {silent: isDesktopView}); // This needs to be silent for desktop view
	},

	/**
	 * Handle "View" button getting clicked
	 */
	onFilterView (event, isDateChanged) {
		event.preventDefault();
		// Because we can click "View" without first clicking "Save Dates"
		// If custom dates are empty, it's ok
		const validation = this.processDateInputs(event);

		// extend the session to keep in sync with server-side session
		this.sessionTimer.extendSession({isTrigger: true});

		// Process free text search
		if (this.model.get('isDesktopView')) {
			if (!isDateChanged) {
				this.freeTextSearchView.updateFreeTextSearch();
			}
		} else {
			this.freeTextSearchView.updateFreeTextSearch();
		}

		// allow search if dates validate and are not too early
		if (validation && !validation.bothDatesTooEarly) {
			this.triggerSearch({ filterSearch: true });
		}
	},

	/**
	 * Change the date value typed by customer into the format we suggest
	 * Do nothing if invalid date
	 * @param event.target - string of target / dom-object of target input
	 */
	replaceCustomDateWithFormatted (event) {
		this.model.formatCustomDateAttributes();
	},

	setupDataForDateSearch () {
		this.unsetSearchFilters();
		this.model.set('isClientSideFiltering', false); // Set client side filtering true. For date searches, we need to make server call
	},

	/**
	 * Handle change events on Filter Model. Keeps UI form inputs synced
	 * with model data.
	 *
	 * @param model {Object} - The filter model object
	 */
	updateFormValues (model) {
		this.$('.js_filters-control-startDate').val(model.get('startDate'));
		this.$('.js_filters-control-endDate').val(model.get('endDate'));
		this.$('.js_filters-control-transactionType option[value=' + model.get('transactionType') + ']')
			.attr('selected', true);

		if (this.model.get('isDesktopView')) {
			this.$('.js_filters-control-freeTextSearch').val(model.get('freeTextSearch') || this.model.get('_freeTextDateSearch'));
		} else {
			this.$('.js_filters-control-freeTextSearch').val(model.get('freeTextSearch'));
		}
	},

	filterSelectedCurrency (model) {
		this.model.set('isClientSideFiltering', true);
		this.triggerSearch();
	},

	/**
	 * Does some pre-search UI updates before calling on the model to do a search
	 *
	 * @param data.nextPageToken {Function} - next page token for "Load
	 * More" use case, which for some reason triggers this function
	 */
	triggerSearch (data = {}) {
		this.$('.js_dateDropdown').removeClass('open');

		if (data.nextPageToken) {
			this.setupDataForDateSearch();
		}

		this.model.doSearch({
			nextPageToken: data.nextPageToken // is undefined if not a load more fetch
		});
	}
});



// WEBPACK FOOTER //
// ./view/activity/filterView.es6